import useSWR from 'swr';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import { useMembership } from 'services';
import { useSubmitHandler } from '@ff-it/form';
import type { EntityContextType } from 'components';
import type { Income } from 'modules/invoicing/income/types';
import type { BuilderData, Handler } from './types';
import { useMemo } from 'react';

// Income-anchored builder: a thin sibling to useBuilder, anchored on an existing DRAFT invoice.
// One operation serves naked-invoice first build, rebuild, and edit-sources — an atomic overwrite.
export function useRebuilder(ctx: EntityContextType<Income>): Handler<BuilderData> {
  const navigate = useNavigate();
  const { slug } = useMembership();
  const { endpoint, item, mutate } = ctx;

  // invoicing/income/<pk>/build/
  const url = `${endpoint}build/`;

  const { data, isLoading, error, mutate: mutateInitialValues } = useSWR<BuilderData>({ url, method: 'GET' });
  const initialValues = useMemo(() => data && { ...data, _useResolvedPO: false }, [data]);

  const submit = useSubmitHandler<BuilderData, Income>(
    { method: 'POST', url },
    {
      onSuccess: (income) => {
        mutateInitialValues(undefined);
        mutate(income, { revalidate: false });
        toast.success('Invoice has been built.');
        navigate(`/${slug}/invoices/income/${income.id}`);
      },
    },
  );

  // The overwrite is destructive only when the draft already holds rows and links.
  const populated = item.rows.length > 0;
  const onSubmit: typeof submit = (values, form) => {
    if (populated && !window.confirm('This will overwrite the existing rows and links. Continue?')) {
      return;
    }
    return submit(values, form);
  };

  if (error) {
    throw error;
  }

  return {
    disabled: false,
    initialValues,
    isLoading,
    onSubmit,
    url,
  };
}
